import React, { useEffect, useRef, useState } from "react";
import { useT } from "@transifex/react";
import { ExerciseTypes, Exercises } from "../../../../../enums/exercises";
import { useTestCreationContext } from "../../../../../context/TestCreationContext";
import InputWithActions from "../../../../../basic/inputWithActions/InputWithActions";
import ExerciseTaskSuggestion from "./exerciseTaskSuggestion/ExerciseTaskSuggestion";
import { getExerciseTypeByQuestionMode } from "../../../../../helpers/ExerciseTypesHelper";

import { Wrapper, ExerciseTaskWrapper, LabelWrapper, Label } from "./StyledComponents";

interface ExerciseTaskProps {
    showTaskSuggestions?: boolean;
}

const ExerciseTask: React.FC<ExerciseTaskProps> = (props) => {
    const { currentExerciseModel, updateCurrentExerciseProperties, setIsTestSynced } = useTestCreationContext();

    const t = useT();

    const t_task = t("Exercise Task", { _tags: "CreateTest,CreateOrEditExercise" });
    const t_freeTextPlaceholder = t("Click here to add an Instruction.", { _tags: "CreateTest,CreateOrEditExercise" });

    const isFreeTextExercise = currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.FREE_TEXT);

    const [triggerFlash, setTriggerFlash] = useState(false);
    const [isEditing, setIsEditing] = useState(isFreeTextExercise && !currentExerciseModel.extraComments);
    const [currentText, setCurrentText] = useState(currentExerciseModel.extraComments ?? "");

    const triggerFlashingAnimation = () => {
        setTriggerFlash(true);
        setTimeout(() => setTriggerFlash(false), 800);
    };

    // Tracked in their own refs so the conversion is detected via the questionMode
    // transition itself, rather than assuming questionMode + extraComments are updated
    // atomically (and so it still fires correctly after a FREE_TEXT → CREATE_TEXT revert).
    const previousQuestionMode = useRef(currentExerciseModel.questionMode);
    const previousExtraComments = useRef(currentExerciseModel.extraComments ?? "");

    useEffect(() => {
        const newExtraComments = currentExerciseModel.extraComments ?? "";

        // "Textaufgabe" auto-converts to CREATE_TEXT when vocab is added; only if it had
        // no task yet the default is filled in, so sync the input and flash it for
        // emphasis. A task the user already typed is untouched — no flash then.
        const justConvertedToCreateText =
            previousQuestionMode.current === ExerciseTypes.get(Exercises.FREE_TEXT) &&
            currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.CREATE_TEXT);
        const defaultWasInjected = !previousExtraComments.current && !!newExtraComments;

        // Removing the last word reverts CREATE_TEXT to FREE_TEXT, which clears the
        // auto-injected default again — sync the input back to the (possibly empty) task.
        const justRevertedToFreeText =
            previousQuestionMode.current === ExerciseTypes.get(Exercises.CREATE_TEXT) &&
            currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.FREE_TEXT);

        previousQuestionMode.current = currentExerciseModel.questionMode;
        previousExtraComments.current = newExtraComments;

        if (justConvertedToCreateText && defaultWasInjected) {
            setCurrentText(newExtraComments);
            setIsEditing(false);
            triggerFlashingAnimation();
        }

        if (justRevertedToFreeText) {
            setCurrentText(newExtraComments);
            setIsEditing(!newExtraComments);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentExerciseModel.questionMode, currentExerciseModel.extraComments]);

    const saveChanges = () => {
        if ((currentText !== "" || isFreeTextExercise) && currentText !== currentExerciseModel.extraComments) {
            updateCurrentExerciseProperties({ extraComments: currentText });
            setIsTestSynced(false);
        } else {
            setCurrentText(currentExerciseModel.extraComments ?? "");
        }

        setIsEditing(false);
    };

    const discardChanges = () => {
        setCurrentText(currentExerciseModel.extraComments ?? "");
        setIsEditing(false);
    };

    return (
        <Wrapper>
            <ExerciseTaskWrapper>
                <LabelWrapper>
                    <Label>{t_task}</Label>
                </LabelWrapper>
                <InputWithActions
                    data-testid="input-with-actions-exercise-task"
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    currentText={currentText}
                    onChange={(e) => setCurrentText(e.target.value)}
                    onSave={saveChanges}
                    onDiscard={discardChanges}
                    placeholder={isFreeTextExercise && !isEditing ? t_freeTextPlaceholder : ""}
                    showWarning={isFreeTextExercise && !currentText}
                    isSaveButtonDisabled={currentText === ""}
                    triggerFlash={triggerFlash}
                />
            </ExerciseTaskWrapper>

            {props.showTaskSuggestions && (
                <ExerciseTaskSuggestion
                    setCurrentText={setCurrentText}
                    exerciseType={
                        getExerciseTypeByQuestionMode(currentExerciseModel.questionMode ?? "") ??
                        Exercises.WRITE_SENTENCE
                    }
                    setIsEditing={setIsEditing}
                    triggerFlashingAnimation={triggerFlashingAnimation}
                />
            )}
        </Wrapper>
    );
};

export default ExerciseTask;
